import React, { useEffect, useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SlideOver } from '../../shared/SlideOver';
import { SelectableRow } from '../../shared/SelectableControl';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Input';
import { StudentData } from '../pages/StudentDetailPanel';
import { GroupId } from '../../../config/systemConfig';
import { error as logError } from '../../../utils/logger';
import { genUuid } from '../../../utils/genUuid';
import { dataLayer } from '../../../data/dataLayer';
import { Users, Calendar, CheckCircle2, Search, ChevronDown, ChevronRight, Check, CheckSquare, UserCheck, Layers, Target, MessageSquare, X } from 'lucide-react';
import { FAMILIES_BY_MODULE, type FamilyDefinition, MODULES, type ModuleId, getModuleIcon } from '../../../content';
import { DjayNarrator } from '../../../mascot';
interface CreatePracticePanelProps {
  open: boolean;
  onClose: () => void;
  students: StudentData[];
  selectedGroup: GroupId;
  teacherId: string;
  classroomId: string | null;
  onSuccess: () => void;
}
type Step = 1 | 2 | 3;
const STEP_CONFIG = [{
  num: 1,
  label: 'Vakgebieden'
}, {
  num: 2,
  label: 'Onderdelen'
}, {
  num: 3,
  label: 'Klaarzetten'
}] as const;
const stepVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 60 : -60,
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -60 : 60,
    opacity: 0
  })
};
export function CreatePracticePanel({
  open,
  onClose,
  students,
  selectedGroup,
  teacherId,
  classroomId,
  onSuccess
}: CreatePracticePanelProps) {
  const [step, setStep] = useState<Step>(1);
  const [direction, setDirection] = useState(1);
  const [selectedModuleIds, setSelectedModuleIds] = useState<ModuleId[]>([]);
  const [selectedFamilyIds, setSelectedFamilyIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSkills, setExpandedSkills] = useState<Record<string, boolean>>({});
  const [selectionMode, setSelectionMode] = useState<'whole' | 'specific' | null>(null);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [deadline, setDeadline] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hideTip, setHideTip] = useState(false);
  useEffect(() => {
    const pref = localStorage.getItem('mp.teacher.hidePracticeTip');
    if (pref === 'true') setHideTip(true);
  }, []);
  const dismissTip = () => {
    setHideTip(true);
    localStorage.setItem('mp.teacher.hidePracticeTip', 'true');
  };
  useEffect(() => {
    if (open) {
      setStep(1);
      setDirection(1);
      setSelectedModuleIds([]);
      setSelectionMode(null);
      setSelectedStudentIds([]);
      setSelectedFamilyIds([]);
      setDeadline('');
      setNote('');
      setTitle('');
      setSearchQuery('');
      setExpandedSkills({});
      setIsSubmitting(false);
      setShowSuccess(false);
      setSaveError(null);
    }
  }, [open]);
  useEffect(() => {
    // When modules change, clear families that are no longer in selected modules
    setSelectedFamilyIds(prev => {
      const validFamilies = selectedModuleIds.flatMap(modId => FAMILIES_BY_MODULE.get(modId) || []).map(f => f.familyId);
      return prev.filter(id => validFamilies.includes(id));
    });
  }, [selectedModuleIds]);
  const handleNext = () => {
    if (step < 3) {
      setDirection(1);
      setStep(s => s + 1 as Step);
    }
  };
  const handleBack = () => {
    if (step > 1) {
      setDirection(-1);
      setStep(s => s - 1 as Step);
    }
  };
  const handleConfirm = async () => {
    if (selectedModuleIds.length === 0) return;
    setIsSubmitting(true);
    setSaveError(null);
    // uuid, geen genId: dit id landt in `practice_assignments.id`.
    const assignmentId = genUuid();
    const wholeClass = selectionMode === 'whole';
    const finalTitle = title.trim() || 'Huiswerk Oefenen';
    const assignment = {
      id: assignmentId,
      teacherId,
      classroomId,
      title: finalTitle,
      moduleIds: selectedModuleIds,
      targetFamilyIds: selectedFamilyIds,
      targetStudentIds: wholeClass ? [] : selectedStudentIds,
      wholeClass,
      questionCount: 20,
      createdAt: Date.now(),
      deadline: deadline ? new Date(deadline).getTime() : null,
      status: 'active' as const,
      completedByStudentIds: [],
      note: note.trim() || undefined
    };
    try {
      // De uitkomst werd genegeerd, dus de leerkracht kreeg "klaargezet!" ook
      // als er niets bewaard was — en merkte dat pas als een leerling zei dat
      // hij geen huiswerk zag staan.
      const saved = await dataLayer.savePracticeAssignment(assignmentId, assignment);
      if (!saved) {
        setSaveError('Het huiswerk kon niet worden klaargezet. Probeer het opnieuw; blijft het misgaan, ververs dan de pagina en controleer of je nog bent ingelogd.');
        return;
      }
      setShowSuccess(true);
    } catch (error) {
      logError('Failed to save practice assignment', error);
      setSaveError('Het huiswerk kon niet worden klaargezet door een onverwachte fout. Probeer het opnieuw.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const toggleModule = (id: ModuleId) => {
    setSelectedModuleIds(prev => prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]);
  };
  const toggleStudent = (id: string) => {
    setSelectedStudentIds(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };
  const isNextDisabled = () => {
    if (step === 1 && selectedModuleIds.length === 0) return true;
    // Step 2 is optional (can just assign whole modules)
    if (step === 3 && selectionMode === null) return true;
    if (step === 3 && selectionMode === 'specific' && selectedStudentIds.length === 0) return true;
    return false;
  };
  const renderStepIndicator = () => <div className="flex items-center gap-1 mb-4" data-id="element-9701" data-mp="2hoottbbv1c4j">
      {STEP_CONFIG.map((s, i) => {
      const isActive = step === s.num;
      const isDone = step > s.num;
      return <Fragment key={s.num} data-id="element-9702" data-mp="28ry2tdc60ngt" data-mpi={i}>
            {i !== 0 && <div className={`flex-1 h-[2px] rounded-full transition-colors duration-300 ${isDone ? 'bg-indigo-500' : 'bg-slate-200'}`} data-id="element-9703" data-mp="23ra405hp32c1" />}
            <div className="flex flex-col items-center gap-1" data-id="element-9704" data-mp="1yt8lblo6l513">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${isDone ? 'bg-indigo-500 text-white' : isActive ? 'bg-indigo-500 text-white ring-4 ring-indigo-100' : 'bg-slate-100 text-slate-400'}`} data-id="element-9705" data-mp="1ttcc980mmneo">
              
                {isDone ? <Check className="w-3.5 h-3.5" data-id="element-9706" data-mp="iq8wbam265g" /> : s.num}
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${isActive ? 'text-indigo-600' : isDone ? 'text-indigo-500' : 'text-slate-400'}`} data-id="element-9707" data-mp="gnhbmtt84ym">
              
                {s.label}
              </span>
            </div>
          </Fragment>;
    })}
    </div>;
  const renderSummaryStrip = () => {
    const hasModules = selectedModuleIds.length > 0;
    const hasFamilies = selectedFamilyIds.length > 0;
    const hasStudents = selectionMode !== null;
    if (!hasModules && step === 1) return null;
    return <motion.div initial={{
      height: 0,
      opacity: 0
    }} animate={{
      height: 'auto',
      opacity: 1
    }} className="border-t border-slate-100 pt-3 pb-1" data-id="element-9708" data-mp="3mpewbwvvrong">
        
        <div className="flex items-center gap-3 flex-wrap" data-id="element-9709" data-mp="11zzbdijceu5q">
          {hasModules && <div className="flex items-center gap-1.5 bg-slate-50 rounded-full px-2.5 py-1 border border-slate-200" data-id="element-9710" data-mp="1223e3x5i9n4f">
              <Target className="w-3 h-3 text-slate-500" data-id="element-9711" data-mp="1s2l688cmzvct" />
              <span className="text-xs font-semibold text-slate-700" data-id="element-9712" data-mp="1s34cuswu6xbj">
                {selectedModuleIds.length} vakken
              </span>
            </div>}
          {hasFamilies ? <div className="flex items-center gap-1.5 bg-slate-50 rounded-full px-2.5 py-1 border border-slate-200" data-id="element-9713" data-mp="21tpiv1eub44h">
              <Layers className="w-3 h-3 text-slate-500" data-id="element-9714" data-mp="1x0ebmz648lc1" />
              <span className="text-xs font-semibold text-slate-700" data-id="element-9715" data-mp="2bqc2xnzcdr5g">
                {selectedFamilyIds.length} onderdelen
              </span>
            </div> : hasModules && <div className="flex items-center gap-1.5 bg-amber-50 rounded-full px-2.5 py-1 border border-amber-200" title="Geen specifieke onderdelen gekozen — leerlingen krijgen vragen uit alle onderdelen van de gekozen vakken" data-id="element-9716" data-mp="26u3hxtygr3qd">
            
                <Layers className="w-3 h-3 text-amber-600" data-id="element-9717" data-mp="qcpjxmxzlr1h" />
                <span className="text-xs font-semibold text-amber-800" data-id="element-9718" data-mp="qfbxq4bh12s5">
                  Alle onderdelen
                </span>
              </div>}
          {hasStudents && <div className="flex items-center gap-1.5 bg-slate-50 rounded-full px-2.5 py-1 border border-slate-200" data-id="element-9719" data-mp="2x2b11terh8vb">
              <UserCheck className="w-3 h-3 text-slate-500" data-id="element-9720" data-mp="322ypq8fipdmd" />
              <span className="text-xs font-semibold text-slate-700" data-id="element-9721" data-mp="2o8ehmmczeajl">
                {selectionMode === 'whole' ? `${students.length} leerlingen` : `${selectedStudentIds.length} leerling(en)`}
              </span>
            </div>}
          {deadline && <div className="flex items-center gap-1.5 bg-slate-50 rounded-full px-2.5 py-1 border border-slate-200" data-id="element-9722" data-mp="2edcolffl4obq">
              <Calendar className="w-3 h-3 text-slate-500" data-id="element-9723" data-mp="25alfhma6m4n9" />
              <span className="text-xs font-semibold text-slate-700" data-id="element-9724" data-mp="25c6b7f88ftmp">
                {new Date(deadline).toLocaleDateString('nl-NL', {
              day: 'numeric',
              month: 'short'
            })}
              </span>
            </div>}
        </div>
      </motion.div>;
  };
  const renderStep1 = () => <div className="flex flex-col h-full" data-id="element-9725" data-mp="7k0tyjasvi17">
      <div className="flex items-center gap-2.5 mb-4" data-id="element-9726" data-mp="1vcn7pnb04kn6">
        <p className="text-sm font-medium text-slate-600" data-id="element-9727" data-mp="1vbu58yj2shau">
          Kies één of meerdere vakgebieden om te oefenen voor{' '}
          <span className="font-bold text-slate-900" data-id="element-9728" data-mp="1leze50p4tj6l">
            groep {selectedGroup}
          </span>
        </p>
      </div>

      {!hideTip && <div className="mb-4 bg-indigo-50 border border-indigo-100 rounded-xl p-3 flex gap-3 relative" data-id="element-9729" data-mp="1qf3qcej0brr0">
          <button onClick={dismissTip} className="absolute top-2 right-2 text-indigo-400 hover:text-indigo-600" data-id="element-9730" data-mp="2e4y3kx78nibs">
        
            <X className="w-4 h-4" data-id="element-9731" data-mp="2j1y99706ej2d" />
          </button>
          <div className="mt-1" data-id="element-9732" data-mp="253q5og8mwji8">
            <DjayNarrator size="xs" emotion="happy" data-id="element-9733" data-mp="252ovsf3cu323" />
          </div>
          <div className="text-sm text-indigo-800 pr-6" data-id="element-9734" data-mp="293ijp8gsrhuz">
            <span className="font-bold" data-id="element-9735" data-mp="296efaq569ug9">Tip van Djay:</span> Modules zijn de
            onderwerpen binnen een vak — bijvoorbeeld 'Breuken' of
            'Werkwoordspelling'.
          </div>
        </div>}

      {/*
       Let op het verschil met het toetspaneel: daar kiest een leerkracht
       precies één vak (keuzerondje), hier mogen er meerdere modules tegelijk
       aan staan (selectievakje). Dat is geen inconsistentie maar het
       wezenlijke onderscheid tussen de twee: een toets meet één ding, een
       oefenopdracht mag over meerdere onderdelen gaan.
       */}
      <div className="grid grid-cols-2 gap-2.5 flex-1 content-start" role="group" aria-label="Kies één of meer onderdelen om te oefenen" data-id="element-9736" data-mp="v5qjywrtirjx">
      
        {MODULES.map(module => {
        const isSelected = selectedModuleIds.includes(module.id);
        const IconComp = getModuleIcon(module.icon);
        return <SelectableRow key={module.id} selected={isSelected} onToggle={() => toggleModule(module.id)} label={module.title} className={`relative p-3 rounded-xl border-2 transition-all cursor-pointer flex flex-col items-center text-center ${isSelected ? 'border-indigo-500 bg-indigo-50/60 shadow-sm' : 'border-slate-200 hover:border-indigo-200 bg-white'}`} data-id="element-9737" data-mp="h7acv4q1mz19">
            
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-2 ${isSelected ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-500'}`} data-id="element-9738" data-mp="9d5toc9g3i4m">
              
                <IconComp className="w-5 h-5" data-id="element-9739" data-mp="i7am304891m9" />
              </div>
              <h3 className={`font-bold text-sm ${isSelected ? 'text-indigo-900' : 'text-slate-900'}`} data-id="element-9740" data-mp="n52lef77bq0h">
              
                {module.title}
              </h3>
              {isSelected && <div className="absolute top-2 right-2" data-id="element-9741" data-mp="3qm2of1m8xahd">
                  <CheckCircle2 className="w-4 h-4 text-indigo-500" data-id="element-9742" data-mp="3qmksgkf5gszu" />
                </div>}
            </SelectableRow>;
      })}
      </div>
    </div>;
  const renderStep2 = () => {
    const allFamilies = selectedModuleIds.flatMap(modId => FAMILIES_BY_MODULE.get(modId) || []);
    const filteredFamilies = allFamilies.filter(f => f.description.toLowerCase().includes(searchQuery.toLowerCase()) || f.skill.toLowerCase().includes(searchQuery.toLowerCase()));
    const grouped: Record<string, typeof allFamilies> = {};
    filteredFamilies.forEach(f => {
      if (!grouped[f.skill]) grouped[f.skill] = [];
      grouped[f.skill].push(f);
    });
    const toggleFamily = (id: string) => {
      setSelectedFamilyIds(prev => prev.includes(id) ? prev.filter(fid => fid !== id) : [...prev, id]);
    };
    const toggleSkillGroup = (familiesInSkill: FamilyDefinition[]) => {
      const ids = familiesInSkill.map(f => f.familyId);
      const allSelected = ids.every(id => selectedFamilyIds.includes(id));
      if (allSelected) {
        setSelectedFamilyIds(prev => prev.filter(id => !ids.includes(id)));
      } else {
        setSelectedFamilyIds(prev => Array.from(new Set([...prev, ...ids])));
      }
    };
    const toggleExpand = (skill: string) => {
      setExpandedSkills(prev => ({
        ...prev,
        [skill]: !prev[skill]
      }));
    };
    const handleSelectAll = () => {
      if (selectedFamilyIds.length === allFamilies.length) {
        setSelectedFamilyIds([]);
      } else {
        setSelectedFamilyIds(allFamilies.map(f => f.familyId));
      }
    };
    const selectedCount = selectedFamilyIds.length;
    const isAllSelected = allFamilies.length > 0 && selectedCount === allFamilies.length;
    return <div className="flex flex-col h-full" data-id="element-9743" data-mp="3v6xgebxqgq38">
        <div className="flex items-center gap-2.5 mb-3" data-id="element-9744" data-mp="3xqjcx0fraq3">
          <p className="text-sm font-medium text-slate-600" data-id="element-9745" data-mp="40csv26w00ay">
            Kies welke onderdelen je leerlingen oefenen.
          </p>
        </div>

        {selectedCount === 0 && <div className="mb-3 flex items-start gap-2.5 p-3 rounded-xl bg-amber-50 border border-amber-200" data-id="element-9746" data-mp="3r4sk15fwhfcw">
            <div className="mt-0.5 text-amber-600" data-id="element-9747" data-mp="3cg67fbhnr1pd">
              <Target className="w-4 h-4" data-id="element-9748" data-mp="3hcmvk5ouxg2g" />
            </div>
            <div className="text-xs text-amber-900 leading-relaxed" data-id="element-9749" data-mp="3hb3dqanjywyk">
              <span className="font-bold" data-id="element-9750" data-mp="33e6r6legw1nh">Nog niets gekozen.</span> Zonder
              selectie krijgen de leerlingen vragen uit{' '}
              <span className="font-bold" data-id="element-9751" data-mp="37h353yt2b9l3">
                alle {allFamilies.length} onderdelen
              </span>{' '}
              van de gekozen{' '}
              {selectedModuleIds.length === 1 ? 'module' : 'modules'} — dus ook
              onderdelen die je misschien niet bedoelde.
            </div>
          </div>}

        <div className="flex items-center justify-between mb-2" data-id="element-9752" data-mp="2ss1nnjt1ym1n">
          <div className="flex items-center gap-2" data-id="element-9753" data-mp="2suwa1gvmmgxp">
            <span className="text-sm font-bold text-indigo-600" data-id="element-9754" data-mp="37osgtbc4v1it">
              {selectedCount}
            </span>
            <span className="text-xs text-slate-500" data-id="element-9755" data-mp="32q7py2irtu71">
              / {allFamilies.length} geselecteerd
            </span>
          </div>
          <button onClick={handleSelectAll} className="flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-700 px-2 py-1 rounded-lg hover:bg-indigo-50 transition-colors" data-id="element-9756" data-mp="3cgithnyjm1de">
            
            <CheckSquare className="w-3.5 h-3.5" data-id="element-9757" data-mp="3lhh1ehc3qt19" />
            {isAllSelected ? 'Deselecteer' : 'Alles'}
          </button>
        </div>

        <div className="relative mb-3" data-id="element-9758" data-mp="2j53y9u5ir14f">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-9759" data-mp="2j1zomh9abd6d" />
          <Input placeholder="Zoek..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 bg-white h-10 text-sm" data-id="element-9760" data-mp="2n31p11extv9x" />
          
        </div>

        <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-1.5 pb-2" data-id="element-9761" data-mp="2wws8frw7jfir">
          {Object.entries(grouped).map(([skill, families]) => {
          const isExpanded = expandedSkills[skill];
          const familyIds = families.map(f => f.familyId);
          const selectedInSkill = familyIds.filter(id => selectedFamilyIds.includes(id)).length;
          const isAllSkillSelected = selectedInSkill === families.length;
          const isPartiallySelected = selectedInSkill > 0 && selectedInSkill < families.length;
          return <div key={skill} className="border border-slate-200 rounded-lg overflow-hidden bg-white" data-id="element-9762" data-mp="1gjbv4axyj2fv">
                
                <div className="flex items-center justify-between min-h-[44px] px-3" data-id="element-9763" data-mp="1dw9jvoblvay7">
                  <div className="flex items-center gap-2 cursor-pointer flex-1 py-2" onClick={() => toggleExpand(skill)} data-id="element-9764" data-mp="195t4hyo85f97">
                    
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" data-id="element-9765" data-mp="1479g7rm5rrdo" /> : <ChevronRight className="w-4 h-4 text-slate-400" data-id="element-9766" data-mp="z7wwhfql30fb" />}
                    <span className="text-sm font-semibold text-slate-700 truncate" data-id="element-9767" data-mp="z5k7iimerpsr">
                      {skill.replace(/_/g, ' ')}
                    </span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${isAllSkillSelected ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-500'}`} data-id="element-9768" data-mp="1rxlxvu77h2hg">
                      
                      {selectedInSkill}/{families.length}
                    </span>
                  </div>
                  <div className="cursor-pointer p-2 -mr-1" onClick={() => toggleSkillGroup(families)} data-id="element-9769" data-mp="bjdpd3j9stot">
                    
                    <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${isAllSkillSelected ? 'bg-indigo-500 border-indigo-500 text-white' : isPartiallySelected ? 'bg-indigo-100 border-indigo-400 text-indigo-500' : 'border-slate-300 bg-white'}`} data-id="element-9770" data-mp="26l79j04qlw8w">
                      
                      {isAllSkillSelected && <Check className="w-3 h-3" data-id="element-9771" data-mp="22iu82nx0wkhx" />}
                      {isPartiallySelected && <div className="w-2 h-0.5 bg-current rounded-full" data-id="element-9772" data-mp="2q9gfccz7e0qv" />}
                    </div>
                  </div>
                </div>

                {isExpanded && <div className="border-t border-slate-100" data-id="element-9773" data-mp="2ua07t38u0g6o">
                    {families.map(f => {
                const isSelected = selectedFamilyIds.includes(f.familyId);
                return <SelectableRow key={f.familyId} selected={isSelected} onToggle={() => toggleFamily(f.familyId)} label={f.description} className="flex items-center gap-3 px-3 py-2.5 min-h-[44px] cursor-pointer transition-colors hover:bg-slate-50 border-b border-slate-50 last:border-b-0" data-id="element-9774" data-mp="2lh6pewy390ol">
                        
                          <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-indigo-500 border-indigo-500 text-white' : 'border-slate-300 bg-white'}`} data-id="element-9775" data-mp="3nbhwjsizkhjp">
                          
                            {isSelected && <Check className="w-3 h-3" data-id="element-9776" data-mp="3s5m3ibs94pks" />}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-9777" data-mp="34frrtxjo5724">
                            <span className={`text-sm truncate block ${isSelected ? 'font-semibold text-slate-900' : 'text-slate-600'}`} data-id="element-9778" data-mp="30d4whmnsh406">
                            
                              {f.description}
                            </span>
                          </div>
                          <span className="text-[10px] text-slate-400 font-bold uppercase flex-shrink-0" data-id="element-9779" data-mp="39fw9vs0869ug">
                            {f.levelCode}
                          </span>
                        </SelectableRow>;
              })}
                  </div>}
              </div>;
        })}
          {filteredFamilies.length === 0 && <div className="text-center py-8 text-slate-400 text-sm" data-id="element-9780" data-mp="1jt7k9taqdy7a">
              <div className="mb-3 flex justify-center" data-id="element-9781" data-mp="1jv24f5e6hmab">
                <DjayNarrator size="md" emotion="thinking" data-id="element-9782" data-mp="1xt8w23sjaq1j" />
              </div>
              Geen resultaten voor "{searchQuery}"
            </div>}
        </div>
      </div>;
  };
  const renderStep3 = () => {
    return <div className="flex flex-col gap-4 pb-4" data-id="element-9783" data-mp="27q4xjpk0mbwl">
        <div className="flex items-center gap-2.5" data-id="element-9784" data-mp="27otszniqx04f">
          <p className="text-sm font-medium text-slate-600" data-id="element-9785" data-mp="22raecmi4ylby">
            Bijna klaar! Kies de leerlingen en voeg details toe.
          </p>
        </div>

        <div data-id="element-9786" data-mp="2chwlrtfp85jk">
          <label className="text-xs font-semibold text-slate-500 mb-1 block" data-id="element-9787" data-mp="2chd5si9lzr86">
            Titel (optioneel)
          </label>
          <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Bijv. Breuken oefenen" className="h-9 text-sm" data-id="element-9788" data-mp="2qiphuilnf8os" />
          
        </div>

        <div className="grid grid-cols-2 gap-2" data-id="element-9789" data-mp="354d4cafbqpat">
          <div onClick={() => setSelectionMode('whole')} className={`p-3 rounded-xl border-2 cursor-pointer transition-all text-center ${selectionMode === 'whole' ? 'border-indigo-500 bg-indigo-50/60' : 'border-slate-200 hover:border-indigo-200 bg-white'}`} data-id="element-9790" data-mp="354wdp2qws5d8">
            
            <Users className={`w-5 h-5 mx-auto mb-1 ${selectionMode === 'whole' ? 'text-indigo-500' : 'text-slate-400'}`} data-id="element-9791" data-mp="304iema7ac5rc" />
            
            <div className={`text-sm font-semibold ${selectionMode === 'whole' ? 'text-indigo-900' : 'text-slate-700'}`} data-id="element-9792" data-mp="2mckcwkfwlc57">
              
              Hele klas
            </div>
            <div className="text-[11px] text-slate-400" data-id="element-9793" data-mp="2ch0iy3kwgnj3">
              {students.length} leerlingen
            </div>
          </div>
          <div onClick={() => setSelectionMode('specific')} className={`p-3 rounded-xl border-2 cursor-pointer transition-all text-center ${selectionMode === 'specific' ? 'border-indigo-500 bg-indigo-50/60' : 'border-slate-200 hover:border-indigo-200 bg-white'}`} data-id="element-9794" data-mp="2hcpp4prfd2od">
            
            <UserCheck className={`w-5 h-5 mx-auto mb-1 ${selectionMode === 'specific' ? 'text-indigo-500' : 'text-slate-400'}`} data-id="element-9795" data-mp="dzgkddj0w7eh" />
            
            <div className={`text-sm font-semibold ${selectionMode === 'specific' ? 'text-indigo-900' : 'text-slate-700'}`} data-id="element-9796" data-mp="47t0bxospsn1">
              
              Selectie
            </div>
            <div className="text-[11px] text-slate-400" data-id="element-9797" data-mp="4nx1sww5mxc">Kies leerlingen</div>
          </div>
        </div>

        {selectionMode === 'specific' && <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-9798" data-mp="rxewsdds9trc">
            <div className="px-3 py-2 bg-slate-50 text-xs font-bold text-slate-600 border-b border-slate-200" data-id="element-9799" data-mp="nvteudme6paa">
              {selectedStudentIds.length} van {students.length} geselecteerd
            </div>
            <div className="max-h-[150px] overflow-y-auto divide-y divide-slate-100" data-id="element-9800" data-mp="38mttyghc10pc">
              {students.map(student => {
            const isSelected = selectedStudentIds.includes(student.id);
            return <SelectableRow key={student.id} selected={isSelected} onToggle={() => toggleStudent(student.id)} label={student.name} className={`flex items-center justify-between px-3 py-2 min-h-[44px] cursor-pointer transition-colors ${isSelected ? 'bg-indigo-50/40' : 'hover:bg-slate-50'}`} data-id="element-9801" data-mp="1j5otjqdipu5n">
                  
                    <div className="flex items-center gap-2.5" data-id="element-9802" data-mp="1wxli5kfz9lxa">
                      <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600" data-id="element-9803" data-mp="21vwtjzlx84hm">
                        {student.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-sm font-medium text-slate-800" data-id="element-9804" data-mp="2buvsgqgvwlrf">
                        {student.name}
                      </span>
                    </div>
                    <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-indigo-500 border-indigo-500 text-white' : 'border-slate-300 bg-white'}`} data-id="element-9805" data-mp="hg2x8sjwxx00">
                    
                      {isSelected && <Check className="w-3 h-3" data-id="element-9806" data-mp="3masz4fwl8iq8" />}
                    </div>
                  </SelectableRow>;
          })}
            </div>
          </div>}

        <div className="flex items-center gap-3" data-id="element-9807" data-mp="5xoysgfsip7">
          <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" data-id="element-9808" data-mp="4mksrj2x8xk" />
          <div className="flex-1" data-id="element-9809" data-mp="3rcqn1nfxo1pi">
            <label className="text-xs font-semibold text-slate-500 mb-0.5 block" data-id="element-9810" data-mp="3r9md09fsebqx">
              Deadline (optioneel)
            </label>
            <Input type="date" value={deadline} onChange={e => setDeadline(e.target.value)} className="h-9 text-sm" min={new Date().toISOString().split('T')[0]} data-id="element-9811" data-mp="2ynjsezjlnw9j" />
            
          </div>
        </div>

        <div className="flex items-start gap-3" data-id="element-9812" data-mp="38kpmmgnig3dz">
          <MessageSquare className="w-4 h-4 text-slate-400 flex-shrink-0 mt-2" data-id="element-9813" data-mp="38j4o6fy2s0au" />
          <div className="flex-1" data-id="element-9814" data-mp="2ku28hdn04hiz">
            <label className="text-xs font-semibold text-slate-500 mb-0.5 block" data-id="element-9815" data-mp="2ktag0bopazwx">
              Bericht voor leerlingen (optioneel)
            </label>
            <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Bijv. Succes met oefenen!" className="w-full text-sm border border-slate-200 rounded-lg p-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-none h-20" data-id="element-9816" data-mp="vdpww9i6xft5" />
            
          </div>
        </div>
      </div>;
  };
  return <SlideOver open={open} onClose={onClose} title="Huiswerk Opgeven" description={<span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold bg-indigo-100 text-indigo-700" data-id="element-9818" data-mp="1e5iobzhm2syr">
          Groep {selectedGroup}
        </span>} width="lg" data-id="element-9817" data-mp="qdc0xptv5wbh">
      
      <div className="flex flex-col h-full" data-id="element-9819" data-mp="153jafg1w8501">
        {showSuccess ? <div className="flex-1 flex flex-col items-center justify-center p-6 text-center" data-id="element-9820" data-mp="1a2w5go2x4idp">
            <motion.div initial={{
          scale: 0.8,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          type: 'spring',
          bounce: 0.5
        }} className="mb-6" data-id="element-9821" data-mp="3putdz228cfio">
            
              <DjayNarrator size="lg" emotion="proud" data-id="element-9822" data-mp="20cmsdenor63j" />
            </motion.div>
            <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.1
        }} className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mb-4" data-id="element-9823" data-mp="1vfmkqzb6tvab">
            
              <CheckCircle2 className="w-8 h-8" data-id="element-9824" data-mp="1hohswje2a7aj" />
            </motion.div>
            <motion.h2 initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.2
        }} className="text-2xl font-black text-slate-900 mb-2" data-id="element-9825" data-mp="1cnkac5p53b8i">
            
              Huiswerk opgegeven! 🎉
            </motion.h2>
            <motion.p initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.3
        }} className="text-slate-600 mb-6 max-w-sm" data-id="element-9826" data-mp="321gv17797flj">
            
              Het huiswerk voor{' '}
              <span className="font-bold" data-id="element-9827" data-mp="orp2ifzryili">
                {selectionMode === 'whole' ? 'de hele klas' : `${selectedStudentIds.length} leerling(en)`}
              </span>{' '}
              is klaargezet —{' '}
              <span className="font-bold" data-id="element-9828" data-mp="xjr44hlky4xd">
                {selectedFamilyIds.length > 0 ? `${selectedFamilyIds.length} onderdelen` : `alle onderdelen van ${selectedModuleIds.length === 1 ? 'het gekozen vak' : 'de gekozen vakken'}`}
              </span>
              .
              {deadline && <>
                  {' '}
                  Deadline:{' '}
                  <span className="font-bold" data-id="element-9829" data-mp="eu2lnnyx0gyw">
                    {new Date(deadline).toLocaleDateString('nl-NL', {
                day: 'numeric',
                month: 'long'
              })}
                  </span>
                  .
                </>}
            </motion.p>
            <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.5
        }} data-id="element-9830" data-mp="1qbapa71qlt20">
            
              <Button onClick={() => {
            onSuccess();
            onClose();
          }} className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 h-11 font-semibold" data-id="element-9831" data-mp="wvzwxkpr1tm4">
              
                Sluiten
              </Button>
            </motion.div>
          </div> : <>
            {renderStepIndicator()}
            <div className="flex-1 overflow-hidden relative" data-id="element-9832" data-mp="1fou3bq9b4deq">
              <AnimatePresence mode="wait" custom={direction} data-id="element-9833" data-mp="1fqeztlf6m6f8">
                <motion.div key={step} custom={direction} variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.2,
              ease: 'easeInOut'
            }} className="absolute inset-0 overflow-y-auto px-0.5" data-id="element-9834" data-mp="1tmsfqq42hzrl">
                
                  {step === 1 && renderStep1()}
                  {step === 2 && renderStep2()}
                  {step === 3 && renderStep3()}
                </motion.div>
              </AnimatePresence>
            </div>
            <div className="flex-shrink-0 bg-white" data-id="element-9835" data-mp="3lb07eia9ls9d">
              {renderSummaryStrip()}
              <div className="flex items-center justify-between pt-3 pb-safe" data-id="element-9836" data-mp="37jbx8kjktdv1">
                <Button variant="ghost" onClick={step === 1 ? onClose : handleBack} className="text-slate-500 hover:text-slate-700 hover:bg-slate-100 h-11 px-4" data-id="element-9837" data-mp="3cieon26mvlkj">
                
                  {step === 1 ? 'Annuleren' : 'Terug'}
                </Button>
                {/* Naast de knop die hem veroorzaakte: op de laatste stap is
                   de bovenkant van het paneel weggescrold. */}
                {saveError && <p role="alert" className="flex-1 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mr-3" data-id="element-9838" data-mp="2tl55lm580a3t">
                
                    {saveError}
                  </p>}
                {step < 3 ? <Button onClick={handleNext} disabled={isNextDisabled()} className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm h-11 px-6 font-semibold" data-id="element-9839" data-mp="rsh0f790ncle">
                
                    Volgende <ChevronRight className="w-4 h-4 ml-1" data-id="element-9840" data-mp="1yk80tpo78mkf" />
                  </Button> : <Button onClick={handleConfirm} disabled={isSubmitting || isNextDisabled()} className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm h-11 px-6 font-semibold" data-id="element-9841" data-mp="23ij9vy6o6t1l">
                
                    {isSubmitting ? 'Bezig...' : 'Opgeven'}
                  </Button>}
              </div>
            </div>
          </>}
      </div>
    </SlideOver>;
}